/**
 * INV-63 provisional Tanzania administrative catalog.
 *
 * This is an immutable, version-pinned Region -> District -> Ward reference
 * artifact. It is deliberately separate from the earlier onboarding starter
 * slice in `tanzania.ts`: this catalog is not an official government source,
 * carries source-supplied provisional ward postal codes (not an official
 * allocation), carries no street data, and excludes Zanzibar pending
 * verification.
 */
import catalogJson from './tanzania-provisional.catalog.json' with { type: 'json' };
import manifestJson from './tanzania-provisional.manifest.json' with { type: 'json' };
const catalog = catalogJson;
export const TANZANIA_PROVISIONAL_MANIFEST = manifestJson;
if (catalog.catalogVersion !== TANZANIA_PROVISIONAL_MANIFEST.catalogVersion) {
    throw new Error('Tanzania catalog and manifest versions must match.');
}
const regionIndex = new Map(catalog.regions.map((region) => [region.code, region]));
export function getTanzaniaCatalogManifest() {
    return TANZANIA_PROVISIONAL_MANIFEST;
}
export function getTanzaniaCatalogVersion() {
    return catalog.catalogVersion;
}
export function isRetainedTanzaniaCatalogVersion(catalogVersion) {
    return catalogVersion === catalog.catalogVersion;
}
export function listTanzaniaCatalogRegions(catalogVersion) {
    if (!isRetainedTanzaniaCatalogVersion(catalogVersion))
        return null;
    return catalog.regions.map(toItem);
}
export function listTanzaniaCatalogDistricts(catalogVersion, regionCode) {
    if (!isRetainedTanzaniaCatalogVersion(catalogVersion))
        return null;
    const region = regionIndex.get(regionCode);
    return region ? region.districts.map(toItem) : null;
}
export function listTanzaniaCatalogWards(catalogVersion, regionCode, districtCode) {
    if (!isRetainedTanzaniaCatalogVersion(catalogVersion))
        return null;
    const district = regionIndex.get(regionCode)?.districts.find((item) => item.code === districtCode);
    return district ? district.wards.map(toItem) : null;
}
/**
 * Resolves a complete structured selection only when its catalog version and
 * every parent-child relationship match the retained immutable artifact.
 */
export function resolveTanzaniaCatalogSelection(selection) {
    if (!isRetainedTanzaniaCatalogVersion(selection.catalogVersion))
        return null;
    const region = regionIndex.get(selection.regionCode);
    if (!region)
        return null;
    const district = region.districts.find((item) => item.code === selection.districtCode);
    if (!district)
        return null;
    const ward = district.wards.find((item) => item.code === selection.wardCode);
    if (!ward)
        return null;
    return {
        catalogVersion: catalog.catalogVersion,
        region: toItem(region),
        district: toItem(district),
        ward: toItem(ward),
    };
}
/**
 * Validation boundary for future server-side address writes. Manual input is
 * intentionally returned unchanged; catalog input is accepted only after an
 * exact versioned hierarchy resolution. No label is accepted from a catalog
 * client payload or silently substituted.
 */
export function validateTanzaniaAdministrativeAddressSelection(selection) {
    if (selection.administrativeMode === 'manual') {
        return { administrativeMode: 'manual', selection };
    }
    const resolved = resolveTanzaniaCatalogSelection(selection);
    return resolved ? { administrativeMode: 'catalog', resolved } : null;
}
function toItem(item) {
    return {
        code: item.code,
        label: item.label,
        ...(item.sourcePostalCode !== undefined ? { sourcePostalCode: item.sourcePostalCode } : {}),
    };
}
//# sourceMappingURL=tanzania-provisional.js.map